import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FoxLogo } from '../FoxLogo';
import { LayoutDashboard, Package, FileText, LifeBuoy, Users, Server, Settings, LogOut, Menu, X, Palette, Globe, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
export function AdminSidebar({
  isOpen,
  setIsOpen
}: SidebarProps) {
  const navigate = useNavigate();
  const {
    adminUser,
    adminLogout
  } = useAuth();
  const adminLinks = [{
    name: 'Overview',
    path: '/admin',
    icon: LayoutDashboard
  }, {
    name: 'Customers',
    path: '/admin/customers',
    icon: Users
  }, {
    name: 'Domain Management',
    path: '/admin/domains',
    icon: Globe
  }, {
    name: 'Packages & Services',
    path: '/admin/packages',
    icon: Package
  }, {
    name: 'Invoices',
    path: '/admin/invoices',
    icon: FileText
  }, {
    name: 'Support Tickets',
    path: '/admin/tickets',
    icon: LifeBuoy
  }, {
    name: 'Staff Management',
    path: '/admin/staff',
    icon: Users
  }, {
    name: 'Server Integrations',
    path: '/admin/servers',
    icon: Server
  }, {
    name: 'Payment Gateways',
    path: '/admin/gateways',
    icon: CreditCard
  }, {
    name: 'Theme Editor',
    path: '/admin/theme',
    icon: Palette
  }, {
    name: 'Settings',
    path: '/admin/settings',
    icon: Settings
  }];
  const handleLogout = async () => {
    await adminLogout();
    navigate('/backend');
  };
  const SidebarContent = () => <div className="flex flex-col h-full bg-slate-950 border-r border-slate-800 w-64" data-id="element-61">
      <div className="p-6 flex items-center gap-3 border-b border-slate-800" data-id="element-62">
        <FoxLogo className="w-8 h-8" data-id="element-63" />
        <div data-id="element-64">
          <h2 className="font-heading font-bold text-white leading-tight" data-id="element-65">
            Fox Line
          </h2>
          <span className="inline-block mt-1 px-2 py-0.5 bg-red-500/20 text-red-500 text-[10px] font-bold uppercase tracking-wider rounded border border-red-500/30" data-id="element-66">
            Admin System
          </span>
        </div>
      </div>

      <div className="flex-1 py-6 px-4 overflow-y-auto" data-id="element-67">
        <nav className="space-y-1" data-id="element-68">
          {adminLinks.map(link => <NavLink key={link.name} to={link.path} end={link.path === '/admin'} onClick={() => setIsOpen(false)} className={({
          isActive
        }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                ${isActive ? 'bg-fox-600/10 text-fox-500 border border-fox-500/20' : 'text-slate-400 hover:text-white hover:bg-slate-900'}
              `} data-id="element-69">
              <link.icon size={18} data-id="element-70" />
              {link.name}
            </NavLink>)}
        </nav>
      </div>

      <div className="p-4 border-t border-slate-800" data-id="element-71">
        {adminUser && <div className="px-4 py-2 mb-2" data-id="element-72">
            <p className="text-xs text-slate-500 truncate" data-id="element-73">{adminUser.email}</p>
          </div>}
        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-900 transition-colors" data-id="element-74">
          <LogOut size={18} data-id="element-75" />
          Sign Out
        </button>
      </div>
    </div>;
  return <>
      {/* Mobile Toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-950 border-b border-slate-800 z-40 flex items-center justify-between px-4" data-id="element-76">
        <div className="flex items-center gap-2" data-id="element-77">
          <FoxLogo className="w-6 h-6" data-id="element-78" />
          <span className="font-heading font-bold text-white" data-id="element-79">Admin</span>
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="text-slate-300 hover:text-white" data-id="element-80">
          {isOpen ? <X size={24} data-id="element-81" /> : <Menu size={24} data-id="element-82" />}
        </button>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed top-0 left-0 h-screen z-30" data-id="element-83">
        <SidebarContent data-id="element-84" />
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-85">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-86" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="lg:hidden fixed top-0 left-0 h-screen z-50" data-id="element-87">
              <SidebarContent data-id="element-88" />
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}